import { db } from './index.js';
import { LIBRARY_CHANGED_EVENT } from '../sync/events.js';
import { toDifficultyLevel } from '../utils/difficulty.js';
import { applyServerTabState } from './tabEdits.js';
import type { DifficultyLevel, TabSong } from '../types/index.js';

export interface DifficultyValues {
  rhythm: DifficultyLevel | null;
  lead: DifficultyLevel | null;
}

const notify = () => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(LIBRARY_CHANGED_EVENT));
};

/** Writes the values to the local row. Respects opts?.pending. Returns the new row. */
export async function setLocalDifficulty(
  id: string,
  values: DifficultyValues,
  opts?: { pending?: boolean }
): Promise<TabSong | undefined> {
  const updated = await db.transaction('rw', db.tabs, async () => {
    const cur = await db.tabs.get(id);
    if (!cur) return undefined;
    const next: TabSong = {
      ...cur,
      rhythmDifficulty: values.rhythm,
      leadDifficulty: values.lead,
      pendingDifficulty: opts?.pending ? 1 : 0
    };
    await db.tabs.put(next);
    return next;
  });
  if (updated) notify();
  return updated;
}

/** Rows of this user with an owner edit not yet accepted by the server. */
export async function getPendingDifficultyRows(userId: string): Promise<TabSong[]> {
  const rows = await db.tabs.where('pendingDifficulty').equals(1).toArray();
  return rows.filter((r) => r.ownerId === userId && !r.removedAt);
}

/**
 * Writes the server's difficulty values to a local row. Wrapper over applyServerTabState.
 */
export async function applyServerDifficulty(
  id: string,
  row: { rhythmDifficulty?: unknown; leadDifficulty?: unknown },
  opts: { force?: boolean; clearPending?: boolean } = {}
): Promise<boolean> {
  const patch: Partial<TabSong> = {};
  if (row.rhythmDifficulty !== undefined) {
    patch.rhythmDifficulty = toDifficultyLevel(row.rhythmDifficulty);
  }
  if (row.leadDifficulty !== undefined) {
    patch.leadDifficulty = toDifficultyLevel(row.leadDifficulty);
  }
  const changed = await applyServerTabState(
    id,
    patch,
    { force: opts.force, skipKinds: opts.force ? [] : undefined }
  );
  if (opts.clearPending) {
    await clearPendingDifficulty(id);
  }
  return changed;
}

/** Clears the queue flag without touching the values (404: no server value to restore). */
export async function clearPendingDifficulty(id: string): Promise<void> {
  await db.tabs.update(id, { pendingDifficulty: 0 });
}
